import React, {useState} from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiPlus, FiEdit2, FiTrash2, FiSave, FiX} = FiIcons;

const OpenerPresets = () => {
  const [presets, setPresets] = useState([
    {
      id: 1,
      name: 'Mobile Optimization',
      template: 'Your mobile conversion rate is 40% lower than desktop users according to industry benchmarks for sites with similar navigation patterns. A responsive redesign focused on your product pages could recover this lost revenue within 30 days. Would you like to see examples of how we\'ve solved this for similar companies?'
    },
    {
      id: 2,
      name: 'UX Improvements',
      template: 'The multi-step form on your services page is likely causing a 25-30% abandonment rate based on UX research patterns. Simplifying to a single-page form could significantly boost your lead capture rates. Have you measured the current completion rate for these forms?'
    },
    {
      id: 3,
      name: 'Performance Issues',
      template: 'Your homepage is currently loading in 5.2 seconds on mobile - research shows this causes 32% of visitors to abandon before seeing your value proposition. Optimizing just three key image assets could improve this by 40%. Would you be interested in a quick analysis of which elements are causing the biggest delays?'
    }
  ]);

  const [editingPreset, setEditingPreset] = useState(null);
  const [newPreset, setNewPreset] = useState({name: '', template: ''});
  const [showNewForm, setShowNewForm] = useState(false);

  const startEditing = (preset) => {
    setEditingPreset({...preset});
  };

  const cancelEditing = () => {
    setEditingPreset(null);
  };

  const saveEditing = () => {
    if (!editingPreset.name || !editingPreset.template) {
      alert('Please fill in all fields');
      return;
    }

    setPresets(presets.map(p => p.id === editingPreset.id ? editingPreset : p));
    setEditingPreset(null);
  };

  const deletePreset = (id) => {
    if (window.confirm('Are you sure you want to delete this preset?')) {
      setPresets(presets.filter(p => p.id !== id));
    }
  };

  const handleNewPreset = () => {
    if (!newPreset.name || !newPreset.template) {
      alert('Please fill in all fields');
      return;
    }

    const newId = Math.max(0, ...presets.map(p => p.id)) + 1;
    setPresets([...presets, {...newPreset, id: newId}]);
    setNewPreset({name: '', template: ''});
    setShowNewForm(false);
  };

  return (
    <motion.div
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      className="opener-presets"
    >
      <div className="presets-header">
        <h3>Value-First Email Templates</h3>
        <button
          className="add-preset-button"
          onClick={() => setShowNewForm(!showNewForm)}
        >
          <SafeIcon icon={showNewForm ? FiX : FiPlus} />
          <span>{showNewForm ? 'Cancel' : 'Add Template'}</span>
        </button>
      </div>

      {showNewForm && (
        <div className="preset-form">
          <div className="form-group">
            <label>Template Name</label>
            <input
              type="text"
              value={newPreset.name}
              onChange={(e) => setNewPreset({...newPreset, name: e.target.value})}
              placeholder="E.g., Mobile Optimization"
              className="form-input"
            />
          </div>
          <div className="form-group">
            <label>Template Text</label>
            <textarea
              value={newPreset.template}
              onChange={(e) => setNewPreset({...newPreset, template: e.target.value})}
              placeholder="Start with a specific, valuable insight that demonstrates your expertise..."
              className="form-textarea"
              rows="3"
            />
          </div>
          <button className="save-preset-button" onClick={handleNewPreset}>
            <SafeIcon icon={FiSave} />
            <span>Save Template</span>
          </button>
        </div>
      )}

      <div className="presets-list">
        {presets.map(preset => (
          <div key={preset.id} className="preset-card">
            {editingPreset && editingPreset.id === preset.id ? (
              <div className="preset-edit-form">
                <div className="form-group">
                  <label>Template Name</label>
                  <input
                    type="text"
                    value={editingPreset.name}
                    onChange={(e) => setEditingPreset({...editingPreset, name: e.target.value})}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Template Text</label>
                  <textarea
                    value={editingPreset.template}
                    onChange={(e) => setEditingPreset({...editingPreset, template: e.target.value})}
                    className="form-textarea"
                    rows="3"
                  />
                </div>
                <div className="edit-actions">
                  <button className="cancel-button" onClick={cancelEditing}>
                    <SafeIcon icon={FiX} />
                    <span>Cancel</span>
                  </button>
                  <button className="save-button" onClick={saveEditing}>
                    <SafeIcon icon={FiSave} />
                    <span>Save Changes</span>
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="preset-header">
                  <h4>{preset.name}</h4>
                  <div className="preset-actions">
                    <button className="action-icon" onClick={() => startEditing(preset)}>
                      <SafeIcon icon={FiEdit2} />
                    </button>
                    <button className="action-icon delete" onClick={() => deletePreset(preset.id)}>
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>
                </div>
                <div className="preset-content">
                  <p>{preset.template}</p>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      <style jsx>{`
        .opener-presets {
          margin-top: 2rem;
        }
        
        .presets-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }
        
        .presets-header h3 {
          font-size: 1.125rem;
          font-weight: 600;
        }
        
        .add-preset-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background-color: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 0.875rem;
          cursor: pointer;
        }
        
        .add-preset-button:hover {
          background-color: #f9fafb;
        }
        
        .preset-form {
          background-color: white;
          padding: 1rem;
          border-radius: 8px;
          box-shadow: var(--shadow-sm);
          margin-bottom: 1rem;
          border-left: 3px solid var(--primary-color);
        }
        
        .form-group {
          margin-bottom: 0.75rem;
        }
        
        .form-group label {
          display: block;
          font-size: 0.875rem;
          font-weight: 500;
          margin-bottom: 0.25rem;
        }
        
        .form-input, .form-textarea {
          width: 100%;
          padding: 0.5rem;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          font-size: 0.875rem;
        }
        
        .form-textarea {
          resize: vertical;
          min-height: 80px;
        }
        
        .save-preset-button {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background-color: var(--primary-color);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          margin-left: auto;
        }
        
        .save-preset-button:hover {
          background-color: var(--primary-dark);
        }
        
        .presets-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1rem;
        }
        
        .preset-card {
          background-color: white;
          border-radius: 8px;
          box-shadow: var(--shadow-sm);
          padding: 1rem;
        }
        
        .preset-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #f3f4f6;
        }
        
        .preset-header h4 {
          font-size: 0.875rem;
          font-weight: 600;
          margin: 0;
        }
        
        .preset-actions {
          display: flex;
          gap: 0.5rem;
        }
        
        .action-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 6px;
          background-color: #f9fafb;
          border: 1px solid #e5e7eb;
          cursor: pointer;
        }
        
        .action-icon:hover {
          background-color: #f3f4f6;
        }
        
        .action-icon.delete:hover {
          background-color: #fee2e2;
          color: #b91c1c;
          border-color: #fecaca;
        }
        
        .preset-content {
          font-size: 0.875rem;
          line-height: 1.5;
          color: var(--text-color);
        }
        
        .preset-edit-form {
          padding-top: 0.5rem;
        }
        
        .edit-actions {
          display: flex;
          justify-content: flex-end;
          gap: 0.5rem;
          margin-top: 1rem;
        }
        
        .cancel-button {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.5rem 1rem;
          background-color: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 0.75rem;
          cursor: pointer;
        }
        
        .save-button {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.5rem 1rem;
          background-color: var(--primary-color);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 0.75rem;
          font-weight: 500;
          cursor: pointer;
        }
        
        @media (max-width: 768px) {
          .presets-list {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default OpenerPresets;